import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { debounce, get, pick, uniqBy } from 'lodash';

import AsyncSelect from 'react-select/async';
import Select from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { faUser } from '@fortawesome/pro-solid-svg-icons';

import TaskFormErrors from './task_form_errors';
import AssociatedModelSelectors from './associated_model_selectors';
import { searchUsers } from '../users/api';
import ManagedInput from 'components/form/managed_input';
import taskPropTypes from 'components/tasks/prop_types';
import { ProjectDatabaseMemberPropTypes } from 'components/project/prop_types';

import { Button } from 'components/ui/button';
import { Form } from 'react-bootstrap-5';

export default class MobileTaskForm extends React.Component {
  static propTypes = {
    assigneeOptions: PropTypes.arrayOf(ProjectDatabaseMemberPropTypes),
    callListsAccess: PropTypes.bool.isRequired,
    currentDatabaseId: PropTypes.number,
    defaultAssociatedModels: PropTypes.array,
    defaultTaskAssignee: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.number
    }).isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
    existingTask: taskPropTypes,
    hideByProjectSelect: PropTypes.bool,
    hideCallListSelect: PropTypes.bool,
    hideContactSelect: PropTypes.bool,
    hideProjectSelect: PropTypes.bool,
    hidePropertySelect: PropTypes.bool,
    hideTagSelect: PropTypes.bool,
    indexView: PropTypes.bool,
    isActivity: PropTypes.bool,
    multipleProjects: PropTypes.bool,
    newTaskFormStyling: PropTypes.bool,
    onCancel: PropTypes.func,
    onDelete: PropTypes.func,
    onSave: PropTypes.func.isRequired
  };

  static defaultProps = {
    assigneeOptions: [],
    currentDatabaseId: null,
    defaultAssociatedModels: [],
    hideByProjectSelect: false,
    hideCallListSelect: true,
    hideContactSelect: false,
    hideProjectSelect: false,
    hidePropertySelect: false,
    hideTagSelect: false,
    newTaskFormStyling: false,
    indexView: true
  };

  constructor(props) {
    super(props);

    this.debouncedLoadAssigneeOptions = debounce(this.loadAssigneeOptions, 250);

    const { defaultAssociatedModels, defaultTaskAssignee, existingTask, isActivity } = props;

    this.state = existingTask ?
      this.loadTaskForm(existingTask, defaultAssociatedModels) :
      this.initializeTaskForm(isActivity, defaultAssociatedModels, defaultTaskAssignee);
  }

  initializeTaskForm = (isActivity, defaultAssociatedModels, defaultTaskAssignee) => {
    return {
      associatedModels: defaultAssociatedModels || [],
      taskType: 'call',
      dueDate: moment().format(),
      assigneeId: defaultTaskAssignee.id || defaultTaskAssignee.value,
      title: '',
      body: '',
      pinned: false,
      logged: !!isActivity,
      taskListItems: [],
      taskTaggedRecords: [],
      reminderDifference: 0
    };
  }

  loadTaskForm = (task, defaultAssociatedModels) => {
    const fieldsToMap = [
      'assigneeId',
      'body',
      'id',
      'logged',
      'pinned',
      'taskType',
      'title',
      'reminderDifference'
    ];

    return {
      ...pick(task, fieldsToMap),
      dueDate: moment(task.dueDate).format(),
      associatedModels: this.createAssociatedModels(task, defaultAssociatedModels),
      taskListItems: task.taskListItems || [],
      taskTaggedRecords: task.taskTaggedRecords || []
    };
  }

  createAssociatedModels = (existingTask, defaultAssociatedModels) => {
    return existingTask.associatedModels.map((model) => {
      return { ...model, default: this.isDefault(defaultAssociatedModels, model) };
    });
  };

  isDefault = (defaultAssociatedModels, model) => {
    return defaultAssociatedModels.some(association => this.isSameModel(model, association));
  };

  isSameModel = (a, b) => a.associatedModelId === b.associatedModelId &&
    a.associatedModelType === b.associatedModelType;

  componentDidMount() {
    const { associatedModels } = this.state;

    const associatedModelsSet = new Set([...associatedModels]);
    const newAssociatedModels = [...associatedModelsSet];
    this.setState({ associatedModels: newAssociatedModels });
  }

  isValid = () => this.props.isActivity ? this.isValidActivity() : this.isValidTask();
  isValidActivity = () => this.state.title && this.state.dueDate;
  isValidTask = () => this.state.title && this.state.dueDate && this.state.assigneeId;

  matchAssociations = (model1, model2) => (
    model1 === model2 || (model1.originalId && model2.originalId && model1.originalId === model2.originalId)
  );
  assigneeOptions = () => this.props.assigneeOptions.map(u => ({ value: u.id, label: u.name }));

  handleInputChange = (event) => {
    const { checked, name, type, value } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    this.setState((prevState) => {
      const newState = { [name]: newValue };

      if (name === 'taskType') {
        if (newValue !== 'call' && prevState.taskListItems.length > 0) {
          newState.taskListItems = [];
        }

        if (newValue === 'note') {
          newState.title = null;
        } else {
          newState.pinned = false;
        }
      }
      return newState;
    });
  };

  handleAssigneeChange = (event) => {
    this.setState({ assigneeId: event.value });
  };

  handleReminderChange = (event) => {
    this.setState({ reminderDifference: event.target.value });
  };

  handleNewAssociatedModel = (model) => {
    this.setState((prevState) => {
      return { associatedModels: [...prevState.associatedModels, model] };
    });
  };

  hasProjectLeaseSpaces = (model1, model2) => {
    return model2.associatedModelType === 'Project' &&
    model1.associatedModelType === 'LeaseSpace' &&
    model1.relatedAssociatedModelId === model2.associatedModelId;
  }

  handleRemoveAssociatedModel = (model) => {
    this.setState((prevState) => {
      const associatedModels = prevState.associatedModels.map(
        m => this.matchAssociations(m, model) || this.hasProjectLeaseSpaces(m, model) ?
          { ...model, _destroy: true } : m
      );
      return { associatedModels: associatedModels };
    });
  };

  handleNewTaskListItem = (item) => {
    this.setState((prevState) => {
      let updateExisting = false;
      const taskListItems = [...prevState.taskListItems];

      for (let i = 0; i < taskListItems.length; i++) {
        if (taskListItems[i].taskListId === item.taskListId) {
          taskListItems[i]._destroy = false;
          updateExisting = true;
          break;
        }
      }

      return { taskListItems: updateExisting ? taskListItems : [...taskListItems, item] };
    });
  };

  handleRemoveTaskListItem = (item) => {
    this.setState((prevState) => {
      const taskListItems = prevState.taskListItems.map(i => i === item ? { ...item, _destroy: true } : i);
      return { taskListItems: taskListItems };
    });
  };

  handleNewTaskTaggedRecord = (item) => {
    this.setState((prevState) => {
      const taskTaggedRecords = prevState.taskTaggedRecords;
      const existingRecord = taskTaggedRecords.find(record => record.taskTagId === item.taskTagId);
      if (existingRecord) existingRecord._destroy = false;

      return { taskTaggedRecords: existingRecord ? taskTaggedRecords : [...taskTaggedRecords, item] };
    });
  }

  handleRemoveTaskTaggedRecord = (item) => {
    this.setState((prevState) => {
      const taskTaggedRecords = prevState.taskTaggedRecords.map(i => i === item ? { ...item, _destroy: true } : i);
      return { taskTaggedRecords: taskTaggedRecords };
    });
  }

  handleSave = () => {
    const { defaultAssociatedModels, defaultTaskAssignee, indexView, isActivity, onSave } = this.props;
    if (isActivity) {
      const itemToSave = this.state;
      itemToSave.logged = true;
      onSave(itemToSave);
    } else {
      onSave(this.state);
    }

    !indexView && this.setState(this.initializeTaskForm(isActivity, defaultAssociatedModels, defaultTaskAssignee));
  };

  handleCancel = () => {
    const { defaultAssociatedModels, defaultTaskAssignee, existingTask, isActivity } = this.props;

    this.setState(
      existingTask ?
        this.loadTaskForm(existingTask, defaultAssociatedModels) :
        this.initializeTaskForm(isActivity, defaultAssociatedModels, defaultTaskAssignee)
    );

    return this.props.onCancel();
  };

  handleDelete = () => {
    this.props.onDelete(this.state);
    this.props.onCancel();
  };

  handleConfirmedDelete = () => {
    this.setState({ showAreYouSure: false });
    this.handleDelete();
  }

  loadAssigneeOptions = (inputValue, callback) => {
    searchUsers(inputValue).then(({ users }) => {
      const options = users.map(user => ({ value: user.id.toString(), label: `${user.name} (${user.email})` }));

      callback(options);
    });
  };

  handleDueDateChange = (dateObj) => {
    this.setState(dateObj);
  }

  shouldRenderAssociatedModel = model => (
    !model._destroy && !(model.default && model.associatedModelType === 'Multiple')
  );

  renderTaskBody() {
    return (
      <div className="form-group">
        <Form.Label>Summary</Form.Label>
        <div className="optional">
          <textarea
            className="task-details text textarea-minimal w-100 px-4 py-3"
            name="body"
            placeholder="Enter summary (required)..."
            type="text"
            value={this.state.body}
            onChange={this.handleInputChange}
          />
        </div>
      </div>
    );
  }

  renderTaskDetails() {
    return (
      <div className="form-group">
        <Form.Label>Title</Form.Label>
        <div className="optional">
          <textarea
            className="task-title text w-100 px-4 py-3"
            name="title"
            placeholder="Enter title (required)..."
            type="text"
            value={this.state.title}
            onChange={this.handleInputChange}
          />
        </div>
      </div>
    );
  }

  renderAssignee() {
    const selectProps = {
      defaultValue: [{
        value: get(this.props, 'existingTask.assigneeId') || this.props.defaultTaskAssignee.id,
        label: get(this.props, 'existingTask.assignee') || this.props.defaultTaskAssignee.name
      }],
      name: 'assigneeId',
      onChange: this.handleAssigneeChange
    };

    return (
      <Form.Group className="form-group">
        <Form.Label>Assigned To</Form.Label>
        {this.assigneeOptions().length > 0 ? (
          <Select options={this.assigneeOptions()} {...selectProps} />
        ) : (
          <AsyncSelect loadOptions={this.debouncedLoadAssigneeOptions} {...selectProps} />
        )}
      </Form.Group>
    );
  }

  renderAssociatedModels() {
    let modelsToRender = this.state.associatedModels.filter(m => this.shouldRenderAssociatedModel(m));
    modelsToRender = uniqBy(
      modelsToRender,
      model => [model.associatedModelType, model.originalId || model.associatedModelId].join('#')
    );
    if (modelsToRender.length === 0) return;

    const modelTags = modelsToRender.map((model) => {
      let displayName = model.name;

      switch (model.associatedModelType) {
      case 'Contacts::Company':
      case 'Contacts::Person': {
        if (this.props.hideContactSelect) return null;
        break;
      }
      case 'Contacts::TaskList': {
        displayName = `List: ${displayName}`;
        break;
      }
      case 'Project': {
        if (this.props.hideProjectSelect) return null;
        displayName = `Project: ${displayName}`;
        break;
      }
      case 'LeaseSpace': {
        displayName = `Space: ${displayName}`;
        break;
      }
      case 'Research::Property': {
        if (this.props.hidePropertySelect) return null;
        break;
      }
      }

      return this.renderTag(
        model.associatedModelId,
        'associated-model',
        model,
        displayName,
        this.handleRemoveAssociatedModel
      );
    });

    return modelTags;
  }

  renderTaskListItems() {
    if (this.props.hideCallListSelect || this.state.taskType != 'call') return;

    return this.state.taskListItems.filter(i => !i._destroy).map(item => (
      this.renderTag(item.taskListId, 'task-list-item', item, `List: ${item.name}`, this.handleRemoveTaskListItem)
    ));
  }

  renderTaskTag() {
    const taskTaggedRecords = this.state.taskTaggedRecords.filter(item => !item._destroy);
    if (taskTaggedRecords.length === 0) return;

    return taskTaggedRecords.map(model => (
      this.renderTag(
        model.taskTagId,
        'task-tagged-record',
        model,
        `Tag: ${model.name}`,
        this.handleRemoveTaskTaggedRecord
      )
    ));
  }

  renderTag(key, name, item, displayName, handleRemove) {
    return (
      <span className="badge badge-primary badge-muted" key={key} name={name}>
        { this.props.isActivity &&
          <FontAwesomeIcon
            className="me-2"
            icon={faUser}
            size="sm"
          />
        }
        {displayName}

        {!item.default &&
          <FontAwesomeIcon
            className="ms-3 clickable me-2"
            icon={faTimes}
            name={`remove-${name}`}
            size="sm"
            onClick={() => handleRemove(item)}
          />
        }
      </span>
    );
  }

  renderTaskTypeSelect() {
    const { taskType } = this.state;
    const { isActivity } = this.props;

    const activeFilter = classNames({
      'btn btn-outline': true,
      'active': true
    });

    const inactiveFilter = classNames({
      'btn btn-outline': true,
      'active': false
    });

    return (
      <Form.Group className="form-group">
        <Form.Label>{isActivity ? 'Type' : ' Task Type'}</Form.Label>
        <div className="task-type-container btn-group">
          <button
            className={taskType === 'call' ? activeFilter : inactiveFilter}
            name="taskType"
            value="call"
            onClick={e => this.handleInputChange(e)}
          >
            Call
          </button>
          <button
            className={taskType === 'email' ? activeFilter : inactiveFilter}
            name="taskType"
            value="email"
            onClick={this.handleInputChange}
          >
            Email
          </button>
          <button
            className={taskType === 'meeting' ? activeFilter : inactiveFilter}
            name="taskType"
            value="meeting"
            onClick={this.handleInputChange}
          >
            Meeting
          </button>
          <button
            className={taskType === 'other' ? activeFilter : inactiveFilter}
            name="taskType"
            value="other"
            onClick={this.handleInputChange}
          >
            Other
          </button>
          <button
            className={taskType === 'critical_date' ? activeFilter : inactiveFilter}
            name="taskType"
            value="critical_date"
            onClick={this.handleInputChange}
          >
            Critical Date
          </button>
        </div>
      </Form.Group>
    );
  }

  renderTaskHeader() {
    const { indexView, existingTask, isActivity, newTaskFormStyling } = this.props;
    const title = isActivity ? 'Activity' : 'Task';
    const existingTaskTitle = (isActivity || newTaskFormStyling) ? 'New ' + title : 'Create ' + title;
    const headerTitle = existingTask ? 'Edit ' + title : existingTaskTitle;

    if (indexView) {
      return (
        <div className="d-flex justify-content-between align-items-center p-4 bg-white border-bottom">
          <h6>{headerTitle}</h6>
          <Button label="Cancel" name="cancel" size="sm" variant="ghost" onClick={this.handleCancel} />
        </div>
      );
    }
  }

  renderDueDate = () => {
    return (
      <Form.Group className="form-group">
        <Form.Label>Due Date</Form.Label>
        <div>
          <ManagedInput
            className="form-control"
            name="dueDate"
            type="datetime"
            value={this.state.dueDate}
            onChange={this.handleDueDateChange}
          />
        </div>
      </Form.Group>
    );
  }

  renderActivityDetails = () => {
    const { isActivity, newTaskFormStyling } = this.props;
    return (
      <Form.Group className="form-group">
        <Form.Label name="detail-label">
          {isActivity && 'Activity Details'}
          {newTaskFormStyling && 'Task Details'}
        </Form.Label>
        <Form.Group className="form-group optional">
          <Form.Control
            className="details-title w-100 h-3 mb-2 px-4 py-3"
            name="title"
            placeholder="Enter title (required)..."
            type="text"
            value={this.state.title}
            onChange={this.handleInputChange}
          />
          <Form.Control
            as="textarea"
            className="details-summary text w-100 px-4 py-3"
            name="body"
            placeholder="Enter any additional details..."
            type="text"
            value={this.state.body}
            onChange={this.handleInputChange}
          />
        </Form.Group>
      </Form.Group>
    );
  }

  renderReminderSelect() {
    return (
      <Form.Group className="form-group">
        <Form.Label>Email Reminder</Form.Label>
        <Form.Control
          as="select"
          className="form-select"
          name="reminder-difference-select"
          value={this.state.reminderDifference}
          onChange={this.handleReminderChange}
        >
          <option value="0">No Reminder</option>
          <option value={moment.duration(1, 'hours').asSeconds()}>One Hour</option>
          <option value={moment.duration(1, 'days').asSeconds()}>One Day</option>
          <option value={moment.duration(1, 'weeks').asSeconds()}>One Week</option>
        </Form.Control>
      </Form.Group>
    );
  }

  // Ask the user to confirm or cancel delete
  renderAreYouSure() {
    const subjectType = this.props.isActivity ? 'activity' : 'task';
    const handleCancelAYS = () => this.setState({ showAreYouSure: false });
    return (
      <div>
        Are you sure you want to delete this {subjectType}?
        <div className="d-inline-flex justify-content-between w-100 mt-3">
          <button className="btn btn-cancel" name="cancel-delete-task" onClick={handleCancelAYS}>
            Cancel
          </button>
          <button className="btn btn-danger ml-4" name="confirm-delete-task" onClick={this.handleConfirmedDelete}>
            Delete
          </button>
        </div>
      </div>
    );
  }

  renderUserActionButtons() {
    if (this.state.showAreYouSure) {
      return this.renderAreYouSure();
    }

    const subjectType = this.props.isActivity ? 'Activity' : 'Task';
    const handleDelete = () => this.setState({ showAreYouSure: true });
    const deleteTaskButton = (
      <Button name="delete" variant="ghost" onClick={handleDelete}>
        <FontAwesomeIcon className="text-black" icon={faTrashAlt} />
        <span className="text-black ps-2">Delete {subjectType}</span>
      </Button>
    );

    return (
      <div className="d-inline-flex flex-column justify-content-between align-items-center w-100 mt-4">
        <Button disabled={!this.isValid()} label="Save" name="save" onClick={this.handleSave} />
        {this.props.existingTask && deleteTaskButton}
      </div>
    );
  }

  renderAssociationTags() {
    return (
      <div className="d-flex flex-wrap gap-1 mb-2">
        {this.renderAssociatedModels()}
        {this.renderTaskListItems()}
        {this.renderTaskTag()}
      </div>
    );
  }

  render() {
    const {
      callListsAccess,
      currentDatabaseId,
      errors,
      hideByProjectSelect,
      hideCallListSelect,
      hideContactSelect,
      hideProjectSelect,
      hidePropertySelect,
      hideTagSelect,
      isActivity,
      newTaskFormStyling,
      indexView,
      multipleProjects
    } = this.props;

    const projectSelected = this.state.associatedModels.findIndex(model => (
      model.associatedModelType === 'Project' && !model._destroy
    )) > -1;

    const isActivityStyling = isActivity || newTaskFormStyling;

    return (
      <div className="contacts-task-form form-vertical-field-container">
        {this.renderTaskHeader()}
        <TaskFormErrors errors={errors} />
        <div className={indexView ? 'px-4 py-6' : 'px-4 py-6 pt-0'}>
          {this.renderTaskTypeSelect()}
          {isActivityStyling && this.renderDueDate()}
          {newTaskFormStyling && this.renderReminderSelect()}
          {isActivityStyling && this.renderActivityDetails()}
          {!isActivityStyling && this.renderTaskDetails()}
          {!isActivityStyling && this.renderTaskBody()}
          {this.renderAssignee()}
          <Form.Group className="form-group">
            <Form.Label>Associated With</Form.Label>
            {isActivityStyling && this.renderAssociationTags()}
            <AssociatedModelSelectors
              allowByProjectSelect={!hideByProjectSelect && (!projectSelected || multipleProjects)}
              allowCallListSelect={!hideCallListSelect}
              allowContactSelect={!hideContactSelect}
              allowProjectSelect={!hideProjectSelect && (!projectSelected || multipleProjects)}
              allowPropertySelect={!hidePropertySelect}
              allowTagSelect={!hideTagSelect}
              associatedModels={this.state.associatedModels}
              callListsAccess={callListsAccess}
              currentDatabaseId={currentDatabaseId}
              isActivity={isActivityStyling}
              taskListItems={this.state.taskListItems}
              taskTaggedRecords={this.state.taskTaggedRecords}
              taskType={this.state.taskType}
              onModelAdded={this.handleNewAssociatedModel}
              onTaskListAdded={this.handleNewTaskListItem}
              onTaskTagAdded={this.handleNewTaskTaggedRecord}
            />
            {!isActivityStyling && this.renderAssociationTags()}
          </Form.Group>
          {this.renderUserActionButtons()}
          <div className="mb-10" />
        </div>
      </div>
    );
  }
}
